import React, { useRef, useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';

import { IconEye, IconLock, IconEyeOff } from '@tabler/icons-react';

import Button from '@/components/Button/Button';
import Input from '@/components/Input/Input';
import { useFormatText } from '@/hooks/useFormatText';
import {
  changePasswordSchema,
  type TChangePassword,
} from '@/schema/changePasswordSchema';
import { changePassword } from '@/service/settingsServices';
import { encryptPassword } from '@/utils/encryption';
import { zodResolver } from '@hookform/resolvers/zod';

const AccountSecurity = () => {
  const { formatText } = useFormatText();
  const [loading, setLoading] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const currentPasswordRef = useRef<HTMLInputElement>(null);
  const newPasswordRef = useRef<HTMLInputElement>(null);
  const confirmPasswordRef = useRef<HTMLInputElement>(null);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<TChangePassword>({
    resolver: zodResolver(changePasswordSchema),
    mode: 'onChange',
  });

  useEffect(() => {
    const currentPass = currentPasswordRef.current;
    const newPass = newPasswordRef.current;
    const confirmPass = confirmPasswordRef.current;

    return () => {
      if (currentPass) currentPass.value = '';
      if (newPass) newPass.value = '';
      if (confirmPass) confirmPass.value = '';
      reset();
    };
  }, [reset]);

  const triggerBrowserPasswordSave = (currentPwd: string, newPwd: string) => {
    const form = document.createElement('form');
    form.method = 'post';
    form.action = '';
    form.style.display = 'none';

    const currentInput = document.createElement('input');
    currentInput.type = 'password';
    currentInput.autocomplete = 'current-password';
    currentInput.value = currentPwd;
    currentInput.name = 'current-password';

    const newInput = document.createElement('input');
    newInput.type = 'password';
    newInput.autocomplete = 'new-password';
    newInput.value = newPwd;
    newInput.name = 'new-password';

    const submitBtn = document.createElement('input');
    submitBtn.type = 'submit';

    form.appendChild(currentInput);
    form.appendChild(newInput);
    form.appendChild(submitBtn);
    document.body.appendChild(form);

    form.addEventListener('submit', (e) => e.preventDefault());
    submitBtn.click();

    setTimeout(() => document.body.removeChild(form), 1000);
  };

  const onSubmit = async (data: TChangePassword) => {
    setLoading(true);
    try {
      const response = await changePassword({
        current_password: encryptPassword(data.currentPassword),
        new_password: encryptPassword(data.newPassword),
        type: 'admin',
      });
      if (response?.status) {
        triggerBrowserPasswordSave(data.currentPassword, data.newPassword);
        reset();
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitClick = () => {
    handleSubmit(onSubmit)();
  };

  return (
    <div className="space-y-6">
      <h3 className="text-base font-semibold text-neutral-primary">
        {formatText('settings.account-security.change-password')}
      </h3>

      <form
        onSubmit={handleSubmit(onSubmit)}
        className="flex flex-col gap-3"
        autoComplete="off"
        data-lpignore="true"
      >
        <Input
          name="currentPassword"
          label={formatText('settings.account-security.current-password')}
          type="text"
          inputStyle={
            !showCurrentPassword
              ? ({ WebkitTextSecurity: 'disc' } as React.CSSProperties)
              : undefined
          }
          register={register}
          isRequired
          autoComplete="off"
          dataLpignore="true"
          dataFormType="other"
          inputRef={currentPasswordRef}
          error={
            errors.currentPassword?.message
              ? formatText(errors.currentPassword.message)
              : undefined
          }
          leftIcon={{
            icon: <IconLock size={20} className="text-neutral-secondary" />,
          }}
          rightIcon={{
            icon: showCurrentPassword ? (
              <IconEyeOff size={20} className="text-neutral-secondary" />
            ) : (
              <IconEye size={20} className="text-neutral-secondary" />
            ),
            onClick: () => setShowCurrentPassword((prev) => !prev),
          }}
          placeholder="••••••••••••"
        />

        <Input
          name="newPassword"
          label={formatText('settings.account-security.new-password')}
          type="text"
          inputStyle={
            !showNewPassword
              ? ({ WebkitTextSecurity: 'disc' } as React.CSSProperties)
              : undefined
          }
          register={register}
          isRequired
          autoComplete="off"
          dataLpignore="true"
          dataFormType="other"
          inputRef={newPasswordRef}
          error={
            errors.newPassword?.message
              ? formatText(errors.newPassword.message)
              : undefined
          }
          leftIcon={{
            icon: <IconLock size={20} className="text-neutral-secondary" />,
          }}
          rightIcon={{
            icon: showNewPassword ? (
              <IconEyeOff size={20} className="text-neutral-secondary" />
            ) : (
              <IconEye size={20} className="text-neutral-secondary" />
            ),
            onClick: () => setShowNewPassword((prev) => !prev),
          }}
          placeholder="••••••••••••"
        />

        <Input
          name="confirmNewPassword"
          label={formatText('settings.account-security.confirm-new-password')}
          type="text"
          inputStyle={
            !showConfirmPassword
              ? ({ WebkitTextSecurity: 'disc' } as React.CSSProperties)
              : undefined
          }
          register={register}
          isRequired
          autoComplete="off"
          dataLpignore="true"
          dataFormType="other"
          inputRef={confirmPasswordRef}
          error={
            errors.confirmNewPassword?.message
              ? formatText(errors.confirmNewPassword.message)
              : undefined
          }
          leftIcon={{
            icon: <IconLock size={20} className="text-neutral-secondary" />,
          }}
          rightIcon={{
            icon: showConfirmPassword ? (
              <IconEyeOff size={20} className="text-neutral-secondary" />
            ) : (
              <IconEye size={20} className="text-neutral-secondary" />
            ),
            onClick: () => setShowConfirmPassword((prev) => !prev),
          }}
          placeholder="••••••••••••"
        />
      </form>

      <Button
        label={formatText('settings.account-security.save-password')}
        variant="primary"
        onClick={handleSubmitClick}
        loading={loading}
      />
    </div>
  );
};

export default AccountSecurity;
